import type { FC } from 'react'
import type { VesselStatusRow, CardMeta } from '../dashboard/types'
import CardInfoButton from './CardInfoButton'

type VesselStatusTableProps = {
  rows: VesselStatusRow[]
  activeTab: 'ALL' | 'AT_SEA' | 'IN_PORT' | 'AT_ANCHOR'
  onTabChange: (tab: 'ALL' | 'AT_SEA' | 'IN_PORT' | 'AT_ANCHOR') => void
  onRowClick?: (row: VesselStatusRow) => void
}

const StatusBadge: FC<{ status: string }> = ({ status }) => {
  const s = (status || '').toUpperCase()
  let bg = 'bg-secondary', txt = 'text-secondary', dot = 'bg-secondary'
  if (s.includes('SEA') || s === 'AT SEA') { bg = 'bg-light-success'; txt = 'text-success'; dot = 'bg-success' }
  else if (s.includes('PORT') || s === 'IN PORT') { bg = 'bg-light-primary'; txt = 'text-primary'; dot = 'bg-primary' }
  else if (s === 'AT ANCHORAGE') { bg = 'bg-light-secondary'; txt = 'text-gray-600'; dot = 'bg-gray-500' }
  return (
    <span className={`badge ${bg} ${txt} d-inline-flex align-items-center gap-1 px-2 py-1`} style={{ fontSize: '10px' }}>
      <span className={`rounded-circle ${dot}`} style={{ width: 6, height: 6 }}></span>
      {status || 'Unknown'}
    </span>
  )
}

const meta: CardMeta = {
  description: 'Active voyages only, with reported vessel status, ports and ETA in the selected scope.',
  endpoint: 'GET /api/vessels/operations/{id}; GET /api/dpr; GET /voyages',
  logic: 'Unknown status remains Unknown. Anchorage is separate from In Port.',
}

const VesselStatusTable: FC<VesselStatusTableProps> = ({ rows, activeTab, onTabChange, onRowClick }) => {
  const filteredRows = rows.filter(r => {
    if (activeTab === 'ALL') return true
    if (activeTab === 'AT_SEA') return r.status === 'At Sea'
    if (activeTab === 'IN_PORT') return r.status === 'In Port'
    if (activeTab === 'AT_ANCHOR') return r.status === 'At Anchorage'
    return true
  })

  const counts = {
    ALL: rows.length,
    AT_SEA: rows.filter(r => r.status === 'At Sea').length,
    IN_PORT: rows.filter(r => r.status === 'In Port').length,
    AT_ANCHOR: rows.filter(r => r.status === 'At Anchorage').length,
  }

  return (
    <div className='flex flex-col flex-1'>
      <div className='card flex-1' style={{ borderRadius: '16px' }}>
        <div className='card-header border-0 d-flex flex-wrap justify-content-between align-items-center pt-4 px-4 pb-0'>
          <div className='d-flex align-items-center gap-2'>
            <h6 className='fw-bold text-dark mb-0' style={{ fontSize: '13px' }}>Vessel Operations Status</h6>
            <CardInfoButton meta={meta} />
          </div>
        </div>
        <div className='d-flex flex-wrap gap-3 px-4 pt-3' style={{ fontSize: '11px' }}>
          {(['ALL', 'AT_SEA', 'IN_PORT', 'AT_ANCHOR'] as const).map(tab => {
            const labels: Record<string, string> = { ALL: 'All', AT_SEA: 'At Sea', IN_PORT: 'In Port', AT_ANCHOR: 'At Anchorage' }
            return (
              <button key={tab}
                className={`btn btn-link text-decoration-none pb-2 px-0 fw-semibold ${activeTab === tab ? 'text-primary border-bottom border-2 border-primary' : 'text-muted'}`}
                style={{ fontSize: '11px', borderRadius: 0 }}
                onClick={() => onTabChange(tab)}
              >
                {labels[tab]} ({counts[tab]})
              </button>
            )
          })}
        </div>
        <div className='card-body pt-2 px-3'>
          <div className='table-responsive' style={{ maxHeight: '320px', overflowY: 'auto' }}>
            <table className='table table-sm align-middle mb-0' style={{ minWidth: '700px' }}>
              <thead>
                <tr style={{ fontSize: '10px' }} className='text-muted text-uppercase'>
                  <th className='ps-2 py-2 fw-semibold'>Vessel</th>
                  <th className='py-2 fw-semibold'>Status</th>
                  <th className='py-2 fw-semibold'>Current Activity</th>
                  <th className='py-2 fw-semibold'>Next Port</th>
                  <th className='py-2 fw-semibold'>ETA</th>
                  <th className='py-2 fw-semibold'>Context</th>
                  <th className='pe-2 py-2 fw-semibold'>Action</th>
                </tr>
              </thead>
              <tbody style={{ fontSize: '11px' }}>
                {filteredRows.length === 0 ? (
                  <tr><td colSpan={7} className='text-center text-muted py-4'>No active voyages found for this filter</td></tr>
                ) : (
                  filteredRows.map((row, i) => (
                    <tr key={`${row.vesselId}-${row.voyageId}`} className='border-top' style={{ cursor: 'pointer' }} onClick={() => onRowClick?.(row)}>
                      <td className='ps-2 py-2 fw-semibold text-dark'>{row.vessel}<div className='text-muted'>{row.voyageNumber || `Voyage #${row.voyageId}`}</div></td>
                      <td className='py-2'><StatusBadge status={row.status} /></td>
                      <td className='py-2 text-muted'>{row.activity}</td>
                      <td className='py-2 fw-semibold text-dark'>{row.nextPort}</td>
                      <td className='py-2 fw-semibold text-dark'>{row.eta}</td>
                      <td className='py-2 text-muted'>{row.context}</td>
                      <td className='pe-2 py-2'>
                        <button className='btn btn-sm btn-primary' style={{ fontSize: '10px', borderRadius: '8px' }}
                          onClick={e => { e.stopPropagation(); onRowClick?.(row) }}>
                          Monitor
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}

export default VesselStatusTable
